// Confirming a received document, one page at a time.
//
// CONSUMER: shared (Workers send route + web prep screen + node-pool tests).
//
// A form out of our library has been mapped once, blank by blank — 2,122
// of them across 27 AAR forms — so when it opens, every box is already
// where a person put it. Their counteroffer has not. It is their paper,
// and the boxes on it come off the suggestion ladder: the PDF's own
// widgets if it has any, the ruled lines it draws, the words beside
// them, and failing all that, a model's reading of a document nobody has
// ever seen.
//
// That is a reasonable way to place a signature line and a terrible way
// to place one silently. A buyer signing the seller's line, or initialing
// a page they did not mean to accept, is not a cosmetic error.
//
// Nate's spec, §6.3: "Review pass (required): the agent moves page by
// page and clicks Confirm page. The package can't be sent until every
// Stew-placed field is confirmed."
//
// WHY PAGES AND NOT FIELDS. A field-by-field checklist on an eleven-page
// counter is forty clicks and nobody reads the fortieth. A page is the
// unit a person actually looks at: you see the whole page, you see every
// box on it at once, and you say yes. Confirming the page confirms what
// is on it, which is the same assertion made in the way it is really
// made.
//
// WHY NOTHING IS STORED. A confirmation is a person saying "the boxes on
// this page are right." Move a box afterwards and that sentence is no
// longer true about the document in front of you — so a confirmation
// saved last Tuesday, against a layout since edited, is an attestation
// nobody made. Keeping them in the prep screen's own session, beside the
// field placements they are about, means they cannot go stale: close the
// screen and you re-place and re-confirm together. A stored attestation
// that might be about an older document is worse than being asked again.
//
// WHY THE SERVER CHECKS SOMETHING IT CANNOT VERIFY. It cannot know
// whether anyone looked. What it can do is refuse to send without the
// assertion, so the gate is not the browser's alone — a stale prep
// screen, a replayed request, or a direct call to the API all have to
// carry it — and record who made it.
//
// Pure and dependency-free, the field-types.ts rule.

import { isReceivedDocument } from "./document-origin";

/** One document as the send request describes it, reduced to the three
 *  things this decision needs. */
export interface ConfirmableDocument {
  documentId: string;
  /** The filename, for the sentence the sender reads. */
  title: string | null;
  /** What the column says. Anything but "received" needs no review. */
  origin: string | null;
  /** Every page of this document, by number. The prep screen sends its
   *  measured page sizes; only the numbers matter here. */
  pageNumbers: readonly number[];
  /** The pages the sender has confirmed, this session. */
  confirmedPages: readonly number[];
}

export interface UnconfirmedDocument {
  documentId: string;
  title: string | null;
  /** Ascending, so "pages 2, 5 and 9" reads the way it is numbered. */
  pages: number[];
}

/**
 * Which pages of which documents still need a look.
 *
 * A document that is not `received` contributes nothing, whatever its
 * confirmations say: we drew those boxes, on a form we control, and
 * asking about them would be friction with no question behind it.
 */
export function unconfirmedReceivedPages(
  documents: readonly ConfirmableDocument[],
): UnconfirmedDocument[] {
  const out: UnconfirmedDocument[] = [];
  for (const doc of documents) {
    if (!isReceivedDocument(doc.origin)) continue;
    const confirmed = new Set(doc.confirmedPages);
    const pages = [...new Set(doc.pageNumbers)].filter((p) => !confirmed.has(p)).sort((a, b) => a - b);
    if (pages.length > 0) {
      out.push({ documentId: doc.documentId, title: doc.title, pages });
    }
  }
  return out;
}

/** True when every received document has had every page confirmed. */
export function receivedPagesAllConfirmed(documents: readonly ConfirmableDocument[]): boolean {
  return unconfirmedReceivedPages(documents).length === 0;
}

/**
 * What to say about it, in the sender's language.
 *
 * Names the pages, because "confirm every page" on an eleven-page
 * counter with one page left is a scavenger hunt. Same reasoning as
 * describePages() on the prep screen, and the same shape of sentence.
 */
export function unconfirmedPagesMessage(unconfirmed: readonly UnconfirmedDocument[]): string | null {
  if (unconfirmed.length === 0) return null;
  const parts = unconfirmed.map((d) => {
    const name = d.title ? `"${d.title}"` : "a document they sent";
    return `${name} (${describePageList(d.pages)})`;
  });
  const which = joinWithAnd(parts);
  return (
    `They wrote ${unconfirmed.length === 1 ? "this one" : "these"}, so the OS placed our signing ` +
    `boxes onto their form. Check ${which} and confirm each page before it goes out.`
  );
}

/** "page 4" · "pages 2 and 5" · "pages 2, 5 and 9". */
export function describePageList(pages: readonly number[]): string {
  if (pages.length === 0) return "no pages";
  if (pages.length === 1) return `page ${pages[0]}`;
  return `pages ${joinWithAnd(pages.map(String))}`;
}

function joinWithAnd(items: readonly string[]): string {
  if (items.length === 0) return "";
  if (items.length === 1) return items[0]!;
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
